import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import Button from "@/components/ui/button";
import ConfirmModal from "@/components/ui/confirm-modal";
import DatePicker from "@/components/ui/datepicker";
import { Checkbox } from "@/components/ui/checkbox";
import {
  LeftChevron,
  ArrowRightLong,
  ArrowLeftLong,
  Pencil,
  Exam,
  Eye,
  Check,
} from "@/components/icons";
import PollQuestionsStep, {
  PollQuestionData,
} from "../components/PollQuestionsStep";
import PollPreviewStep from "../components/PollPreviewStep";
import {
  pollsApi,
  buildSurveyQuestionsPayload,
  flattenPollQuestionIds,
  getMatrixQuestionError,
  groupPollQuestionsForForm,
} from "../services/polls.api";
import { SurveyStatus } from "../types/polls.types";
import moment from "moment";
import type { UpdateSurveyDto } from "../types/polls.types";
import styles from "./CreatePollPage.module.scss";

const STEPS = [
  { key: "basic", labelKey: "createPoll.steps.basicInfo", icon: Pencil },
  {
    key: "questions",
    labelKey: "createPoll.steps.pollQuestions",
    icon: Exam,
  },
  { key: "preview", labelKey: "createPoll.steps.preview", icon: Eye },
] as const;

interface BasicInfoFormData {
  pollName: string;
  pollDescription: string;
  introText: string;
  closingText: string;
  startDate: string;
  endDate: string;
  isAnonymous: boolean;
}

function dateToISOStart(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}T00:00:00.000Z`;
}

function dateToISOEnd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}T23:59:59.000Z`;
}

function parseISODate(value: string): Date | null {
  if (!value || value.trim() === "") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * PATCH /api/admin/surveys/{id} payload oluşturur.
 * Backend davranışı:
 * - questions gönderilir ve length >= 1 ise: Mevcut sorular silinir, gönderilenler eklenir (tamamen değiştirilir)
 * - questions gönderilmezse: Sorular aynen kalır, sadece name/description/startDate/endDate güncellenir
 * - isDraft: true ise status: 0 (DRAFT) eklenir
 * - isPublish: true ise status: 1 (ASSIGNED) eklenir
 */
function buildUpdateSurveyPayload(
  formData: BasicInfoFormData,
  questions: PollQuestionData[],
  options?: {
    includeQuestions?: boolean;
    isDraft?: boolean;
    isPublish?: boolean;
  },
): UpdateSurveyDto {
  const payload: UpdateSurveyDto = {
    name: formData.pollName.trim(),
    description: formData.pollDescription.trim() || "",
    introText: formData.introText.trim(),
    closingText: formData.closingText.trim(),
  };
  if (options?.isDraft) {
    payload.status = SurveyStatus.DRAFT;
  } else if (options?.isPublish) {
    payload.status = SurveyStatus.ASSIGNED;
  }
  const includeQuestions = options?.includeQuestions !== false;
  if (includeQuestions && questions.length >= 1) {
    payload.questions = buildSurveyQuestionsPayload(questions);
  }
  if (formData.startDate.trim()) {
    const d = parseISODate(formData.startDate);
    if (d) payload.startDate = dateToISOStart(d);
  }
  if (formData.endDate.trim()) {
    const d = parseISODate(formData.endDate);
    if (d) payload.endDate = dateToISOEnd(d);
  }
  return payload;
}

const EditPollPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pollId } = useParams<{ pollId: string }>();
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<BasicInfoFormData>({
    pollName: "",
    pollDescription: "",
    introText: "",
    closingText: "",
    startDate: "",
    endDate: "",
    isAnonymous: false,
  });
  const [questions, setQuestions] = useState<PollQuestionData[]>([]);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  // Backend'de kayıtlı soru ID'leri — yeni eklenenler reorder çağrısından
  // hariç kalsın diye tutuyoruz.
  const serverQuestionIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!pollId) return;
    setLoading(true);
    pollsApi
      .getDetailById(pollId)
      .then((detail) => {
        // PollDetail.startDate/endDate formatDate ile "DD.MM.YYYY" formatında
        const startIso =
          detail.startDate && detail.startDate !== "-"
            ? moment(detail.startDate, "DD.MM.YYYY").toISOString()
            : "";
        const endIso =
          detail.endDate && detail.endDate !== "-"
            ? moment(detail.endDate, "DD.MM.YYYY").toISOString()
            : "";
        setFormData({
          pollName: detail.pollName,
          pollDescription: detail.description,
          introText: detail.introText,
          closingText: detail.closingText,
          startDate: startIso,
          endDate: endIso,
          isAnonymous: detail.isAnonymous ?? false,
        });
        const loaded = groupPollQuestionsForForm(detail.questions);
        setQuestions(loaded);
        serverQuestionIdsRef.current = new Set(flattenPollQuestionIds(loaded));
      })
      .catch(() => {
        toast.error(t("editPoll.loadError"));
        navigate("/dashboard/polls");
      })
      .finally(() => setLoading(false));
  }, [pollId, navigate, t]);

  const handleGoBack = () => {
    navigate("/dashboard/polls");
  };

  const breadcrumbItems = [
    {
      label: t("createPoll.breadcrumb.polls"),
      onClick: () => navigate("/dashboard/polls"),
    },
    {
      label: t("editPoll.breadcrumb.editPoll"),
    },
  ];

  const handleInputChange = (
    field: keyof BasicInfoFormData,
    value: string | boolean,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const validateBeforeSubmit = (): boolean => {
    if (!formData.pollName.trim()) {
      toast.error(
        t("createPoll.validation.pollNameRequired"),
      );
      return false;
    }
    const matrixError = getMatrixQuestionError(questions);
    if (matrixError) {
      toast.error(t(matrixError));
      return false;
    }
    return true;
  };

  const handleSaveDraft = async () => {
    if (!pollId || !validateBeforeSubmit()) return;
    setIsSavingDraft(true);
    try {
      const payload = buildUpdateSurveyPayload(formData, questions, {
        isDraft: true,
      });
      await pollsApi.update(pollId, payload);
      toast.success(
        t("editPoll.draftSuccess"),
      );
      navigate(`/dashboard/polls/${pollId}/detail`);
    } catch {
      toast.error(
        t("editPoll.draftError"),
      );
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleUpdate = () => {
    if (!validateBeforeSubmit()) return;
    setIsUpdateModalOpen(true);
  };

  const handleConfirmUpdate = async () => {
    if (!pollId) return;
    setIsUpdating(true);
    try {
      const payload = buildUpdateSurveyPayload(formData, questions, {
        isPublish: true,
      });
      await pollsApi.update(pollId, payload);
      toast.success(
        t("editPoll.updateSuccess"),
      );
      setIsUpdateModalOpen(false);
      navigate(`/dashboard/polls/${pollId}/detail`);
    } catch {
      toast.error(
        t("editPoll.updateError"),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const getEstimatedDuration = () => {
    const totalSeconds = questions.length * 30;
    const minutes = Math.ceil(totalSeconds / 60);
    return `${minutes} dk`;
  };

  const handleQuestionsChange = (updatedQuestions: PollQuestionData[]) => {
    setQuestions(updatedQuestions);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Header title={t("polls.title")} />
        <div className={styles.container}>
          <div style={{ padding: 24, textAlign: "center", color: "#666" }}>
            {t("common.loading")}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Header title={t("polls.title")} />

      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <div className={styles.breadcrumbContainer}>
            <div
              role="button"
              tabIndex={0}
              className={styles.backButton}
              onClick={handleGoBack}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleGoBack();
                }
              }}
              aria-label={t("common.back")}
            >
              <LeftChevron width={20} height={20} />
            </div>
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <div className={styles.stepperContainer}>
          {currentStep > 0 && (
            <Button
              type="button"
              variant="text"
              color="secondary"
              className={styles.inlineBackButton}
              startIcon={<ArrowLeftLong width={20} height={20} />}
              onClick={handlePrev}
            >
              {t("common.back")}
            </Button>
          )}
          <div className={styles.stepperRow}>
            <div className={styles.stepper}>
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === currentStep;
                const isChecked = index < currentStep;
                const connectorStatus =
                  index < STEPS.length - 1
                    ? currentStep > index
                      ? "completed"
                      : currentStep === index
                        ? "current"
                        : "future"
                    : null;
                return (
                  <React.Fragment key={step.key}>
                    <div
                      role="button"
                      tabIndex={0}
                      className={`${styles.step} ${isActive ? styles.stepActive : ""} ${isChecked ? styles.stepChecked : ""}`}
                      onClick={() => setCurrentStep(index)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setCurrentStep(index);
                        }
                      }}
                    >
                      <span className={styles.stepIcon}>
                        {isChecked ? (
                          <Check width={24} height={24} color="#fff" />
                        ) : (
                          <Icon width={24} height={24} />
                        )}
                      </span>
                      <span className={styles.stepLabel}>
                        {t(
                          step.labelKey,
                          step.key === "basic"
                            ? "Temel Bilgiler"
                            : step.key === "questions"
                              ? t("editPoll.steps.questions")
                              : t("editPoll.steps.preview"),
                        )}
                      </span>
                    </div>
                    {connectorStatus && (
                      <div className={styles.stepConnectorWrapper} aria-hidden>
                        <div
                          className={`${styles.stepConnector} ${connectorStatus === "completed" ? styles.stepConnectorCompleted : connectorStatus === "current" ? styles.stepConnectorCurrent : ""}`}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div className={styles.nextButtonWrap}>
            {currentStep < STEPS.length - 1 && (
              <Button
                type="button"
                variant="text"
                color="primary"
                endIcon={<ArrowRightLong width={20} height={20} />}
                onClick={handleNext}
              >
                {t("createPoll.next")}
              </Button>
            )}
            {currentStep === STEPS.length - 1 && (
              <div className={styles.actionButtons}>
                <Button
                  variant="soft"
                  color="primary"
                  className={styles.draftButton}
                  onClick={handleSaveDraft}
                  loading={isSavingDraft}
                  disabled={isUpdating}
                >
                  {t("editPoll.actions.draft")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className={styles.publishButton}
                  onClick={handleUpdate}
                  disabled={isSavingDraft}
                >
                  {t("editPoll.actions.update")}
                </Button>
              </div>
            )}
          </div>
        </div>

        {currentStep === 0 && (
          <div className={styles.formCard}>
            <h2 className={styles.cardTitle}>
              {t("createPoll.basicInfo.title")}
            </h2>
            <p className={styles.cardDescription}>
              {t("createPoll.basicInfo.description")}
            </p>
            <div className={styles.formGrid}>
              <div className={styles.formField}>
                <Input
                  label={t("createPoll.basicInfo.pollName")}
                  placeholder={t("createPoll.basicInfo.pollNamePlaceholder")}
                  value={formData.pollName}
                  maxLength={255}
                  onChange={(e) =>
                    handleInputChange("pollName", e.target.value)
                  }
                />
              </div>
              <div className={styles.formField}>
                <Textarea
                  label={t("createPoll.basicInfo.pollDescription")}
                  placeholder={t("createPoll.basicInfo.pollDescriptionPlaceholder")}
                  value={formData.pollDescription}
                  maxLength={2000}
                  onChange={(e) =>
                    handleInputChange("pollDescription", e.target.value)
                  }
                />
              </div>
              <div className={styles.formField}>
                <Textarea
                  label={t("createPoll.basicInfo.introText")}
                  placeholder={t("createPoll.basicInfo.introTextPlaceholder")}
                  value={formData.introText}
                  maxLength={5000}
                  rows={5}
                  onChange={(e) => handleInputChange("introText", e.target.value)}
                />
              </div>
              <div className={styles.formField}>
                <Textarea
                  label={t("createPoll.basicInfo.closingText")}
                  placeholder={t("createPoll.basicInfo.closingTextPlaceholder")}
                  value={formData.closingText}
                  maxLength={5000}
                  rows={2}
                  onChange={(e) => handleInputChange("closingText", e.target.value)}
                />
              </div>
              <div className={styles.dateRow}>
                <div className={styles.formField}>
                  <DatePicker
                    label={t("createPoll.basicInfo.startDate")}
                    placeholder={t("createPoll.basicInfo.datePlaceholder")}
                    value={parseISODate(formData.startDate)}
                    onChange={(value) => {
                      const date = Array.isArray(value) ? value[0] : value;
                      handleInputChange(
                        "startDate",
                        date ? dateToISOStart(date) : "",
                      );
                    }}
                    format="dd.MM.yyyy"
                    locale="tr"
                    variant="outlined"
                  />
                </div>
                <div className={styles.formField}>
                  <DatePicker
                    label={t("createPoll.basicInfo.endDate")}
                    placeholder={t("createPoll.basicInfo.datePlaceholder")}
                    value={parseISODate(formData.endDate)}
                    minDate={parseISODate(formData.startDate) ?? undefined}
                    onChange={(value) => {
                      const date = Array.isArray(value)
                        ? (value[1] ?? value[0])
                        : value;
                      handleInputChange(
                        "endDate",
                        date ? dateToISOEnd(date) : "",
                      );
                    }}
                    format="dd.MM.yyyy"
                    locale="tr"
                    variant="outlined"
                  />
                </div>
              </div>
              <div className={styles.anonymousField}>
                <Checkbox
                  label={t("createPoll.basicInfo.isAnonymous")}
                  value={formData.isAnonymous}
                  disabled
                  onChange={(event) =>
                    handleInputChange("isAnonymous", event.target.checked)
                  }
                />
                <p className={styles.anonymousHint}>
                  {formData.isAnonymous
                    ? t("editPoll.anonymous.locked")
                    : t("editPoll.anonymous.notAnonymous")}
                </p>
              </div>
            </div>
          </div>
        )}

        {currentStep === 1 && (
          <PollQuestionsStep
            questions={questions}
            onQuestionsChange={handleQuestionsChange}
            surveyId={pollId}
            serverQuestionIds={serverQuestionIdsRef.current}
          />
        )}

        {currentStep === 2 && (
          <PollPreviewStep formData={formData} questions={questions} />
        )}
      </div>

      <ConfirmModal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        onConfirm={handleConfirmUpdate}
        title={t("editPoll.updateModal.title")}
        message=""
        confirmText={t("editPoll.updateModal.confirm")}
        cancelText={t("createPoll.publishModal.cancel")}
        loading={isUpdating}
        pollInfo={{
          pollName:
            formData.pollName ||
            t("createPoll.publishModal.defaultPollName"),
          questionCount: questions.length,
          duration: getEstimatedDuration(),
          description:
            formData.pollDescription ||
            t("createPoll.publishModal.noDescription"),
        }}
      />
    </DashboardLayout>
  );
};

export default EditPollPage;
